import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Turnstile } from '@/components/ui/Turnstile';

export function NewsletterForm({ className = "mt-8" }: { className?: string }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;

    if (!turnstileToken) {
      setStatus('error');
      setErrorMessage('Please complete the Turnstile verification.');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          turnstileToken,
          company_website: companyWebsite,
        })
      });
      
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to subscribe.');
      }

      setStatus('success');
      setEmail('');
      setFirstName('');
      setLastName('');
      setTurnstileToken('');
      
      // Log conversion event to Google Analytics
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'sign_up', {
          method: 'newsletter',
          event_category: 'engagement',
          event_label: 'Newsletter Modal Signup'
        });
      }
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Something went wrong. Please try again or email office@enneainternational.com.');
      setTurnstileToken('');
      setResetKey(prev => prev + 1);
    }
  };

  return (
    <div className={className}>
      <span className="block font-semibold text-ground uppercase tracking-wider text-sm mb-4 text-center" role="heading" aria-level={2}>
        Subscribe to our Newsletter
      </span>
      <p className="text-sm text-ground/70 font-light mb-6 max-w-xs text-center">
        Get the latest insights on behavioral development and team effectiveness.
      </p>
      
      {status === 'success' ? (
        <div className="bg-rule-3/30 text-rule-3 border border-rule-3/40 p-3 text-sm">
          Thank you for subscribing!
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 w-full max-w-xs">
          {/* Honeypot */}
          <input
            type="text"
            name="company_website"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
            style={{ display: 'none' }}
            tabIndex={-1}
            autoComplete="off"
          />

          <div className="flex gap-3">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              disabled={status === 'loading'}
              className="w-1/2 bg-white/5 border border-white/10 text-ground px-4 py-3 text-sm focus:outline-none focus:border-rule-3 focus:ring-1 focus:ring-rule-3 disabled:opacity-50 transition-colors text-center"
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              disabled={status === 'loading'}
              className="w-1/2 bg-white/5 border border-white/10 text-ground px-4 py-3 text-sm focus:outline-none focus:border-rule-3 focus:ring-1 focus:ring-rule-3 disabled:opacity-50 transition-colors text-center"
            />
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            required
            disabled={status === 'loading'}
            className="bg-white/5 border border-white/10 text-ground px-4 py-3 text-sm focus:outline-none focus:border-rule-3 focus:ring-1 focus:ring-rule-3 disabled:opacity-50 transition-colors text-center w-full"
          />
          {status === 'error' && (
            <p className="text-red-400 text-xs">{errorMessage}</p>
          )}

          <Turnstile 
            onVerify={(token) => { setTurnstileToken(token); if (status === 'error') setStatus('idle'); }}
            onExpire={() => { setTurnstileToken(''); setStatus('error'); setErrorMessage('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
            onError={() => { setTurnstileToken(''); setStatus('error'); setErrorMessage('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
            resetKey={resetKey} 
          />

          <Button 
            type="submit" 
            variant="outline" 
            className="w-full border-white/20 text-white hover:bg-white/10 hover:text-white h-11 disabled:opacity-50"
            disabled={status === 'loading' || !turnstileToken}
          >
            {status === 'loading' ? 'Subscribing...' : (!turnstileToken ? 'Verifying…' : 'Subscribe')}
          </Button>
        </form>
      )}
    </div>
  );
}
